/**
 * Menu Configuration - Hardcoded Menu Structure with Lucide Icons
 *
 * This file defines the entire application menu structure.
 * All icons use Lucide for thin, Notion-style icons.
 */

import type { MenuItem } from '../types';
import { BETA_AGENTS_ORG_IDS, BETA_VOICE_AGENTS_ORG_IDS, LEAD_ENRICHMENT_ORG_IDS } from './betaOrgs';

export const MENU_CONFIG: MenuItem[] = [
  // Dashboard
  {
    id: 'dashboard',
    to: '/dashboard',
    icon: 'LayoutDashboard',
    iconLibrary: 'lucide',
    label: 'Dashboard',
    order: 1,
    hidden: false
  },

  // Attività (My Tasks)
  {
    id: 'my-tasks',
    to: '/my-tasks',
    icon: 'CheckSquare',
    iconLibrary: 'lucide',
    label: 'Attività',
    order: 2,
    hidden: false
  },

  // CRM Section
  {
    id: 'crm',
    icon: 'Users',
    iconLibrary: 'lucide',
    label: 'CRM',
    order: 3,
    hidden: false,
    children: [
      {
        id: 'leads',
        to: '/leads',
        icon: 'UserPlus',
        iconLibrary: 'lucide',
        label: 'Leads',
        order: 1,
        hidden: false
      },
      {
        id: 'clients',
        to: '/clients',
        icon: 'Users',
        iconLibrary: 'lucide',
        label: 'Clienti',
        order: 2,
        hidden: false
      },
      {
        id: 'contacts',
        to: '/contacts',
        icon: 'BookUser',
        iconLibrary: 'lucide',
        label: 'Rubrica',
        order: 3,
        hidden: false
      },
      {
        id: 'partners',
        to: '/partners',
        icon: 'Handshake',
        iconLibrary: 'lucide',
        label: 'Partner',
        order: 4,
        hidden: false
      }
      // Reportistica: niente voce propria dal 20/10/2026 (sera). Le dashboard stanno in Dashboard,
      // la configurazione in Impostazioni > Dashboard e report.
      // Segnalatori: niente voce propria dal 16/10/2026, si aprono da Partner
      // (selettore Partner | Segnalatori in testa alla pagina). Vedi Layout.tsx.
    ]
  },

  // Vendite Section
  {
    id: 'sales',
    icon: 'DollarSign',
    iconLibrary: 'lucide',
    label: 'Vendite',
    order: 4,
    hidden: false,
    children: [
      {
        id: 'deals',
        to: '/deals',
        icon: 'FileText',
        iconLibrary: 'lucide',
        label: 'Offerte',
        order: 1,
        hidden: false
      },
      {
        id: 'commissions',
        to: '/commissions',
        icon: 'Percent',
        iconLibrary: 'lucide',
        label: 'Provvigioni',
        order: 2,
        hidden: false
      }
    ]
  },

  // Servizi & Magazzino Section
  {
    id: 'services-inventory',
    icon: 'Package',
    iconLibrary: 'lucide',
    label: 'Servizi & Magazzino',
    order: 5,
    hidden: false,
    children: [
      {
        id: 'services',
        to: '/services',
        icon: 'Boxes',
        iconLibrary: 'lucide',
        label: 'Servizi',
        order: 1,
        hidden: false
      },
      {
        id: 'inventory',
        to: '/magazzino',
        icon: 'Warehouse',
        iconLibrary: 'lucide',
        label: 'Magazzino',
        order: 2,
        hidden: false
      }
    ]
  },

  // Projects & Tools Section
  {
    id: 'activities',
    icon: 'FolderKanban',
    iconLibrary: 'lucide',
    label: 'Progetti & Strumenti',
    order: 6,
    hidden: false,
    children: [
      {
        id: 'calendar',
        to: '/calendar',
        icon: 'Calendar',
        iconLibrary: 'lucide',
        label: 'Calendario',
        order: 1,
        hidden: false
      },
      {
        id: 'projects',
        to: '/projects',
        icon: 'Briefcase',
        iconLibrary: 'lucide',
        label: 'Progetti',
        order: 2,
        hidden: false
      },
      {
        id: 'meetings',
        to: '/meetings',
        icon: 'Video',
        iconLibrary: 'lucide',
        label: 'Registrazioni',
        order: 3,
        hidden: false
      },
      {
        id: 'tickets',
        to: '/tickets',
        icon: 'LifeBuoy',
        iconLibrary: 'lucide',
        label: 'Ticket',
        order: 4,
        hidden: false
      },
      {
        id: 'wiki',
        to: '/wiki',
        icon: 'BookOpen',
        iconLibrary: 'lucide',
        label: 'Wiki',
        order: 5,
        hidden: false
      }
    ]
  },

  // Fatturazione Section
  {
    id: 'invoicing',
    icon: 'Receipt',
    iconLibrary: 'lucide',
    label: 'Fatturazione',
    order: 8,
    hidden: false,
    children: [
      {
        id: 'fattureincloud',
        to: '/fattureincloud',
        icon: 'FileSpreadsheet',
        iconLibrary: 'lucide',
        label: 'Fatture in Cloud',
        order: 1,
        hidden: false
      },
      {
        id: 'service-costs',
        to: '/services/costs',
        icon: 'TrendingUp',
        iconLibrary: 'lucide',
        label: 'Costi e Margini',
        order: 2,
        hidden: false,
        resource: 'client_expenses',
        action: 'view'
      }
    ]
  },

  // Marketing Section — container con header "MARKETING" + 2 voci flat (ADS + Automazione)
  {
    id: 'marketing',
    icon: 'Megaphone',
    iconLibrary: 'lucide',
    label: 'Marketing',
    order: 9.5,
    hidden: false,
    resource: 'marketing',
    action: 'view',
    children: [
      {
        id: 'ads',
        to: '/ads',
        icon: 'Megaphone',
        iconLibrary: 'lucide',
        label: 'ADS',
        order: 1,
        hidden: false,
        resource: 'marketing',
        action: 'view'
      },
      {
        id: 'marketing-automation',
        to: '/automation',
        icon: 'Zap',
        iconLibrary: 'lucide',
        label: 'Automazione',
        order: 2,
        hidden: false,
        resource: 'settings_automation',
        action: 'view'
      },
      {
        id: 'ai-agents',
        to: '/agents',
        icon: 'Bot',
        iconLibrary: 'lucide',
        label: 'Agenti AI',
        order: 3,
        hidden: false,
        resource: 'agents',
        action: 'view',
        badge: 'beta',
        _betaOrgIds: BETA_AGENTS_ORG_IDS,
      },
      {
        id: 'voice-agents',
        to: '/agents/voice',
        icon: 'PhoneCall',
        iconLibrary: 'lucide',
        label: 'Agenti vocali',
        order: 4,
        hidden: false,
        resource: 'voice_agents',
        action: 'view',
        badge: 'beta',
        _betaOrgIds: BETA_VOICE_AGENTS_ORG_IDS,
      },
      {
        id: 'lead-enrichment',
        to: '/settings/enrichment',
        icon: 'Sparkles',
        iconLibrary: 'lucide',
        label: 'Arricchimento Lead',
        order: 5,
        hidden: false,
        resource: 'lead_enrichment',
        action: 'view',
        _betaOrgIds: LEAD_ENRICHMENT_ORG_IDS,
      }
    ]
  },

  // Comunicazione Section
  {
    id: 'communication',
    icon: 'MessageCircle',
    iconLibrary: 'lucide',
    label: 'Comunicazione',
    order: 8.5,
    hidden: false,
    children: [
      {
        id: 'whatsapp',
        to: '/whatsapp',
        icon: 'MessageSquare',
        iconLibrary: 'lucide',
        label: 'Messaggi',
        order: 1,
        hidden: false
      },
      {
        id: 'gmail',
        to: '/gmail',
        icon: 'Mail',
        iconLibrary: 'lucide',
        label: 'Gmail',
        order: 1.5,
        hidden: false
      },
      {
        id: 'newsletter',
        to: '/newsletter',
        icon: 'Send',
        iconLibrary: 'lucide',
        label: 'Newsletter',
        order: 2,
        hidden: false
      },
      {
        id: 'surveys',
        to: '/surveys',
        icon: 'ClipboardList',
        iconLibrary: 'lucide',
        label: 'Sondaggi',
        order: 3,
        hidden: false
      },
      {
        id: 'lead-forms',
        to: '/lead-forms',
        icon: 'FileInput',
        iconLibrary: 'lucide',
        label: 'Form Lead',
        order: 4,
        hidden: false
      },
      {
        id: 'qr-codes',
        to: '/qr-codes',
        icon: 'QrCode',
        iconLibrary: 'lucide',
        label: 'QR Code',
        order: 5,
        hidden: false,
        resource: 'qr_codes',
        action: 'view'
      },
      {
        id: 'screen-recordings',
        to: '/recordings',
        icon: 'Clapperboard',
        iconLibrary: 'lucide',
        label: 'Video',
        order: 6,
        hidden: false,
        resource: 'screen_recordings',
        action: 'view'
      },
      {
        id: 'bookings',
        to: '/bookings',
        icon: 'CalendarCheck',
        iconLibrary: 'lucide',
        label: 'Prenotazioni',
        order: 7,
        hidden: false,
        resource: 'booking',
        action: 'view'
      },
    ]
  },

  // Impostazioni - rimossa dal menu principale, è in basso
  {
    id: 'settings',
    to: '/settings',
    icon: 'Settings',
    iconLibrary: 'lucide',
    label: 'Impostazioni',
    order: 11,
    hidden: true  // Nascosto perché ora è in basso
  }
];

/**
 * Get menu items with proper filtering
 * Can be extended to support user-specific customizations
 */
export function getMenuItems(): MenuItem[] {
  return MENU_CONFIG;
}
